import type { Customer, ForecastResult } from '@/domain';

export interface BreakdownRow {
  key: string;
  label: string;
  qty: number;
  workload_hours: number;
  share_pct: number;
  count: number;
}

interface ByCustomerInput {
  results: ForecastResult[];
  customers: Customer[];
}

export const reportService = {
  byCustomer({ results, customers }: ByCustomerInput): BreakdownRow[] {
    const byCust = new Map<string, BreakdownRow>();
    for (const r of results) {
      const c = customers.find((cu) => cu.id === r.customer_id);
      const key = r.customer_id;
      const cur = byCust.get(key) ?? {
        key,
        label: c?.name ?? key,
        qty: 0,
        workload_hours: 0,
        share_pct: 0,
        count: 0,
      };
      cur.qty += r.forecast_qty;
      cur.workload_hours += r.total_workload_hours;
      cur.count += 1;
      byCust.set(key, cur);
    }
    return finalize(byCust);
  },

  byEventType(results: ForecastResult[]): BreakdownRow[] {
    const byType = new Map<string, BreakdownRow>();
    for (const r of results) {
      const cur = byType.get(r.event_type) ?? {
        key: r.event_type,
        label: r.event_type,
        qty: 0,
        workload_hours: 0,
        share_pct: 0,
        count: 0,
      };
      cur.qty += r.forecast_qty;
      cur.workload_hours += r.total_workload_hours;
      cur.count += 1;
      byType.set(r.event_type, cur);
    }
    return finalize(byType);
  },

  byProductGroup({
    results,
    customers,
  }: ByCustomerInput): BreakdownRow[] {
    // Approximate product-group share by multiplying customer.product_mix × forecast qty.
    const m = new Map<string, BreakdownRow>();
    for (const r of results) {
      const c = customers.find((cu) => cu.id === r.customer_id);
      if (!c) continue;
      const totalShare = c.product_mix.reduce((s, p) => s + p.share, 0) || 1;
      for (const pm of c.product_mix) {
        const share = pm.share / totalShare;
        const key = pm.product_group;
        const cur = m.get(key) ?? {
          key,
          label: key,
          qty: 0,
          workload_hours: 0,
          share_pct: 0,
          count: 0,
        };
        cur.qty += r.forecast_qty * share;
        cur.workload_hours += r.total_workload_hours * share;
        cur.count += 1;
        m.set(key, cur);
      }
    }
    // Round qty for display
    for (const row of m.values()) row.qty = Math.round(row.qty);
    return finalize(m);
  },

  byTier({ results, customers }: ByCustomerInput): BreakdownRow[] {
    const m = new Map<string, BreakdownRow>();
    for (const r of results) {
      const c = customers.find((cu) => cu.id === r.customer_id);
      const tier = c?.tier ?? 'LONGTAIL';
      const cur = m.get(tier) ?? {
        key: tier,
        label: tier,
        qty: 0,
        workload_hours: 0,
        share_pct: 0,
        count: 0,
      };
      cur.qty += r.forecast_qty;
      cur.workload_hours += r.total_workload_hours;
      cur.count += 1;
      m.set(tier, cur);
    }
    return finalize(m);
  },

  byDate(results: ForecastResult[]): BreakdownRow[] {
    const m = new Map<string, BreakdownRow>();
    for (const r of results) {
      const cur = m.get(r.date) ?? {
        key: r.date,
        label: r.date,
        qty: 0,
        workload_hours: 0,
        share_pct: 0,
        count: 0,
      };
      cur.qty += r.forecast_qty;
      cur.workload_hours += r.total_workload_hours;
      cur.count += 1;
      m.set(r.date, cur);
    }
    return Array.from(m.values()).sort((a, b) => a.key.localeCompare(b.key));
  },
};

function finalize(map: Map<string, BreakdownRow>): BreakdownRow[] {
  const arr = Array.from(map.values()).sort((a, b) => b.qty - a.qty);
  const total = arr.reduce((s, r) => s + r.qty, 0) || 1;
  for (const r of arr) r.share_pct = (r.qty / total) * 100;
  return arr;
}
